// api
import { logoutUser } from 'shared/api/auth';
import { getAuthAPI } from 'shared/api/authResolverLoader';
import { clearGuestInitialized, isGuestSessionMarked } from 'shared/api/grahakSession';

// helpers
import storage from 'shared/helpers/storage';
import { clearFreeGiftsSessionKey } from 'shared/components/CartCTA/helpers';

// constants
import { TOASTER_TYPES } from 'shared/components/Toast';

type ShowToastFn = (args: { type: TOASTER_TYPES; message: string }) => void;

interface LogoutOptions {
  successMessage: string;
  errorMessage: string;
  redirectPath?: string;
}

const DEFAULT_LOGOUT_MESSAGES: LogoutOptions = {
  successMessage: 'User logged out successfully!',
  errorMessage: 'Something went wrong!',
};

/**
 * Local cleanup half of logout: clear local user-specific data, app storage,
 * cart session keys, Grahak markers, then re-bootstrap a guest session before
 * reloading. This keeps the next boot-time /user call behind a valid guest
 * session whenever Grahak is enabled.
 */
export const finalizeLogoutLocal = async (
  showToast: ShowToastFn,
  clearUserData: () => void,
  toastMessage: string,
  toastType: TOASTER_TYPES,
  options: Pick<LogoutOptions, 'redirectPath'> = {}
): Promise<void> => {
  clearUserData();
  storage.clear();
  clearFreeGiftsSessionKey();
  clearGuestInitialized();
  showToast({ type: toastType, message: toastMessage });

  try {
    const AuthAPI = await getAuthAPI();
    const result = await AuthAPI.initGuestSession({ forceGuest: true });
    if (result?.status !== 'skipped' && !isGuestSessionMarked()) {
      return;
    }
  } catch (err) {
    void err;
    return;
  }

  if (typeof window !== 'undefined') {
    if (options.redirectPath) {
      window.location.assign(options.redirectPath);
      return;
    }

    window.location.reload();
  }
};

/**
 * Drive the logout API and dispatch to the right cleanup branch:
 *   - 2xx success      -> finalizeLogoutLocal + success toast
 *   - 2xx logical fail -> error toast, no cleanup
 *   - 401/403 reject   -> finalizeLogoutLocal because the server session is
 *     already gone and the user's logout intent should still complete locally
 *   - any other reject -> error toast, no cleanup
 */
export const handleLogoutClick = async (
  showToast: ShowToastFn,
  clearUserData: () => void,
  options: Partial<LogoutOptions> = {}
): Promise<void> => {
  const {
    successMessage = DEFAULT_LOGOUT_MESSAGES.successMessage,
    errorMessage = DEFAULT_LOGOUT_MESSAGES.errorMessage,
    redirectPath,
  } = options;

  try {
    const res = await logoutUser();
    if (res?.status === 'success') {
      await finalizeLogoutLocal(
        showToast,
        clearUserData,
        res?.response?.message || successMessage,
        TOASTER_TYPES.SUCCESS,
        { redirectPath }
      );
      return;
    }

    showToast({
      type: TOASTER_TYPES.ERROR,
      message: res?.response?.message || errorMessage,
    });
  } catch (e: any) {
    const statusCode = e?.statusCode || e?.status || e?.response?.status;
    const statusText = typeof e === 'string' ? e : e?.message;
    const sessionAlreadyDead =
      statusCode === 401 ||
      statusCode === 403 ||
      statusText === 'Unauthorized' ||
      statusText === 'Forbidden';

    if (sessionAlreadyDead) {
      await finalizeLogoutLocal(showToast, clearUserData, successMessage, TOASTER_TYPES.SUCCESS, {
        redirectPath,
      });
      return;
    }

    showToast({
      type: TOASTER_TYPES.ERROR,
      message: statusText || errorMessage,
    });
  }
};
